import React from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeftIcon } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import LocationPicker from "@/components/crime/LocationPicker";
import { Button } from "@/components/ui/button";
import CrimeReportForm, { CrimeFormValues } from "@/components/crime/CrimeReportForm";
import { uploadAllFiles } from "@/services/fileUploadService";

const ReportCrime = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [currentLocation, setCurrentLocation] = React.useState<{
    lat: number;
    lng: number;
    name?: string;
  } | null>(null);
  const [imageFiles, setImageFiles] = React.useState<File[]>([]);
  const [videoFiles, setVideoFiles] = React.useState<File[]>([]);
  const [uploadProgress, setUploadProgress] = React.useState(0);

  // Removed the automatic geolocation detection code

  const handleAddImages = (files: File[]) => {
    setImageFiles([...imageFiles, ...files]);
  };

  const handleRemoveImage = (index: number) => {
    setImageFiles(imageFiles.filter((_, i) => i !== index));
  };

  const handleAddVideos = (files: File[]) => {
    setVideoFiles([...videoFiles, ...files]);
  };

  const handleRemoveVideo = (index: number) => {
    setVideoFiles(videoFiles.filter((_, i) => i !== index));
  };

  const handleLocationSelected = (location: { lat: number; lng: number; name?: string }) => {
    setCurrentLocation(location);
    toast({
      title: "Location Updated",
      description: location.name || `Location set to coordinates (${location.lat.toFixed(4)}, ${location.lng.toFixed(4)})`,
    });
  };

  const onSubmit = async (data: CrimeFormValues) => {
    if (!user) {
      toast({
        title: "Authentication Error",
        description: "You must be logged in to report a crime",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    setUploadProgress(0);
    
    try {
      const fileUploads = await uploadAllFiles(
        imageFiles, 
        videoFiles, 
        setUploadProgress
      );
      
      const { error } = await supabase.from("crime_reports").insert({
        user_id: user.id,
        category: data.category,
        description: data.description,
        latitude: data.latitude,
        longitude: data.longitude,
        location_name: data.locationName || null,
        image_paths: fileUploads.images.length > 0 ? fileUploads.images : null,
        video_paths: fileUploads.videos.length > 0 ? fileUploads.videos : null,
      });

      if (error) throw error;

      toast({
        title: "Report Submitted",
        description: "Your crime report has been submitted successfully.",
      });
      
      navigate("/dashboard", { 
        state: { fromReport: true } 
      });
    } catch (error: any) {
      toast({
        title: "Submission Failed",
        description: error.message || "Failed to submit report. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
      setUploadProgress(0);
    }
  };

  return (
    <div className="flex min-h-screen flex-col">
      <Navbar />
      <main className="flex-1 container py-8">
        <div className="flex items-center mb-6">
          <Button 
            variant="ghost" 
            className="mr-2" 
            onClick={() => navigate("/")}
          >
            <ChevronLeftIcon className="h-4 w-4 mr-1" />
            Back
          </Button>
          <h1 className="text-3xl font-bold">Report a Crime</h1>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="order-2 md:order-1 space-y-6">
            <CrimeReportForm
              onSubmit={onSubmit}
              isSubmitting={isSubmitting}
              uploadProgress={uploadProgress}
              currentLocation={currentLocation}
              imageFiles={imageFiles}
              videoFiles={videoFiles}
              onAddImages={handleAddImages}
              onRemoveImage={handleRemoveImage}
              onAddVideos={handleAddVideos}
              onRemoveVideo={handleRemoveVideo}
            />
          </div>

          <div className="order-1 md:order-2 bg-gray-50 dark:bg-gray-800 rounded-lg overflow-hidden h-[500px] shadow-md">
            <LocationPicker 
              initialLocation={currentLocation} 
              onLocationSelected={handleLocationSelected} 
            />
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ReportCrime;
